/**
 * ProgressRightRail.tsx — Live progress sidebar for Answer Lane
 *
 * W21: Primary Answer Surface
 *
 * Shows:
 *   - Helix (composite_score) gauge — big number + band label
 *   - Macro counter: "You have answered N of TOTAL questions..."
 *   - Next gate at risk: lowest-band CP not yet green
 *   - Next best question suggestion: highest-weight unanswered
 *
 * On mobile (<1024px): rendered as a bottom sheet pull-up via CSS.
 * Helix view is read-only.
 *
 * Canon palette: gray #9CA3AF, red #DC2626, orange #EA580C, yellow #EAB308, green #16A34A
 */

import type { CpProgress, Question } from "@/hooks/useAnswerLane";
import { GATE_COLORS, CP_SHORT_NAMES } from "@/hooks/useAnswerLane";

interface Props {
  compositeScore: number | null;
  totalAnswered: number;
  totalQuestions: number;
  accountName?: string;
  cpProgress: CpProgress[];
  nextBestQuestion: Question | null;
  onJumpToQuestion?: (questionKey: string, cpNumber: number) => void;
  /** When true, renders as a mobile bottom sheet */
  bottomSheet?: boolean;
  bottomSheetOpen?: boolean;
  onBottomSheetToggle?: () => void;
}

// M-PACT 5-band color
function scoreColor(score: number): string {
  if (score >= 80) return "#16A34A";
  if (score >= 65) return "#16A34A";
  if (score >= 50) return "#EAB308";
  if (score >= 30) return "#EA580C";
  return "#DC2626";
}

function scoreBandLabel(score: number): string {
  if (score >= 80) return "Strong";
  if (score >= 65) return "Qualified";
  if (score >= 50) return "Developing";
  if (score >= 30) return "Early Stage";
  return "Not Qualifying";
}

function gateColor(status: string): string {
  const s = (status || "").toLowerCase();
  if (s === "green") return GATE_COLORS.green;
  if (s === "yellow") return GATE_COLORS.yellow;
  if (s === "orange") return GATE_COLORS.orange;
  if (s === "red") return GATE_COLORS.red;
  return GATE_COLORS.gray;
}

// Find lowest-band at-risk CP (not yet green)
function findAtRiskCp(cpProgress: CpProgress[]): CpProgress | null {
  const priority = ["red", "orange", "yellow", "gray"];
  for (const band of priority) {
    const cp = cpProgress.find((p) => (p.status || "").toLowerCase() === band);
    if (cp) return cp;
  }
  return null;
}

function RailContent({
  compositeScore,
  totalAnswered,
  totalQuestions,
  accountName,
  cpProgress,
  nextBestQuestion,
  onJumpToQuestion,
}: Omit<Props, "bottomSheet" | "bottomSheetOpen" | "onBottomSheetToggle">) {
  const score = compositeScore ?? 0;
  const atRisk = findAtRiskCp(cpProgress);
  const pct = totalQuestions > 0 ? Math.round((totalAnswered / totalQuestions) * 100) : 0;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 20,
        padding: "20px 18px",
        overflowY: "auto",
        height: "100%",
      }}
    >
      {/* Helix score gauge */}
      <div
        style={{
          border: "1px solid #E5E5E7",
          borderRadius: 10,
          padding: "16px 18px",
          background: "#FAFAF7",
        }}
      >
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.16em",
            color: "#9CA3AF",
            marginBottom: 8,
          }}
        >
          Composite score
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: 8,
            marginBottom: 6,
          }}
        >
          <span
            style={{
              fontSize: 48,
              fontWeight: 800,
              color: scoreColor(score),
              lineHeight: 1,
            }}
          >
            {Math.round(score)}
          </span>
          <span
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: scoreColor(score),
            }}
          >
            / 100
          </span>
        </div>
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: scoreColor(score),
            marginBottom: 10,
          }}
        >
          {scoreBandLabel(score)}
        </div>
        {/* Score bar */}
        <div
          style={{
            height: 5,
            borderRadius: 3,
            background: "#E5E5E7",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${Math.min(100, score)}%`,
              background: scoreColor(score),
              borderRadius: 3,
              transition: "width 0.4s",
            }}
          />
        </div>
        <p
          style={{
            fontSize: 10,
            color: "#9CA3AF",
            marginTop: 6,
            fontStyle: "italic",
          }}
        >
          Read-only — score updates as you answer
        </p>
      </div>

      {/* Macro counter */}
      <div
        style={{
          border: "1px solid #E5E5E7",
          borderRadius: 10,
          padding: "14px 16px",
          background: "#FFFFFF",
        }}
      >
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.16em",
            color: "#9CA3AF",
            marginBottom: 8,
          }}
        >
          Overall Progress
        </div>
        <p
          style={{
            fontSize: 13,
            fontWeight: 500,
            color: "#0B0B0F",
            lineHeight: 1.5,
            margin: 0,
          }}
        >
          You have answered{" "}
          <strong style={{ fontWeight: 800 }}>
            {totalAnswered} of {totalQuestions}
          </strong>{" "}
          questions across CP1–CP10
          {accountName ? ` for ${accountName}` : ""}.
        </p>
        <div
          style={{
            height: 4,
            borderRadius: 2,
            background: "#E5E5E7",
            marginTop: 10,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${pct}%`,
              background: pct >= 80 ? "#16A34A" : pct >= 50 ? "#EAB308" : "#EA580C",
              borderRadius: 2,
              transition: "width 0.4s",
            }}
          />
        </div>
        <p
          style={{
            fontSize: 11,
            color: "#9CA3AF",
            marginTop: 6,
          }}
        >
          {pct}% complete
        </p>
      </div>

      {/* Next gate at risk */}
      {atRisk && (
        <div
          style={{
            border: `1px solid ${gateColor(atRisk.status)}`,
            borderRadius: 10,
            padding: "14px 16px",
            background: "#FFFBEB",
          }}
          role="alert"
          aria-label="Next gate at risk"
        >
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.16em",
              color: gateColor(atRisk.status),
              marginBottom: 8,
            }}
          >
            Next Checkpoint at Risk
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 7,
              marginBottom: 4,
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: gateColor(atRisk.status),
                flexShrink: 0,
              }}
            />
            <span style={{ fontSize: 13, fontWeight: 700, color: "#0B0B0F" }}>
              {CP_SHORT_NAMES[atRisk.cp_number] ?? `CP${atRisk.cp_number}`}
            </span>
          </div>
          <p style={{ fontSize: 12, color: "#6B6B70", margin: 0 }}>
            {atRisk.answered} of {atRisk.total} questions answered — this Checkpoint is holding back your score.
          </p>
        </div>
      )}

      {/* Next best question */}
      {nextBestQuestion && (
        <div
          style={{
            border: "1px solid #E5E5E7",
            borderRadius: 10,
            padding: "14px 16px",
            background: "#FFFFFF",
          }}
        >
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.16em",
              color: "#9CA3AF",
              marginBottom: 8,
            }}
          >
            Suggested Next Question
          </div>
          <p
            style={{
              fontSize: 12,
              fontWeight: 500,
              color: "#0B0B0F",
              lineHeight: 1.5,
              margin: "0 0 10px",
            }}
          >
            {nextBestQuestion.question_text}
          </p>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span style={{ fontSize: 10, color: "#9CA3AF" }}>
              {CP_SHORT_NAMES[nextBestQuestion.cp_number] ?? `CP${nextBestQuestion.cp_number}`}
            </span>
            {onJumpToQuestion && (
              <button
                type="button"
                onClick={() =>
                  onJumpToQuestion(
                    nextBestQuestion.question_key,
                    nextBestQuestion.cp_number,
                  )
                }
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#DC2626",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  padding: 0,
                }}
              >
                Go to this question
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ProgressRightRail({
  bottomSheet = false,
  bottomSheetOpen = false,
  onBottomSheetToggle,
  ...rest
}: Props) {
  if (!bottomSheet) {
    return (
      <aside
        style={{
          width: 320,
          flexShrink: 0,
          borderLeft: "1px solid #E5E5E7",
          background: "#FAFAF7",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
        aria-label="Progress panel"
      >
        <RailContent {...rest} />
      </aside>
    );
  }

  // Mobile bottom sheet
  return (
    <>
      {/* Pull-up tab */}
      <div
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          background: "#FFFFFF",
          borderTop: "1px solid #E5E5E7",
          boxShadow: "0 -4px 16px rgba(0,0,0,0.08)",
        }}
      >
        <button
          type="button"
          onClick={onBottomSheetToggle}
          style={{
            width: "100%",
            padding: "12px 16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "transparent",
            border: "none",
            cursor: "pointer",
            fontSize: 13,
            fontWeight: 600,
            color: "#0B0B0F",
          }}
          aria-expanded={bottomSheetOpen}
        >
          <span>Progress</span>
          <span style={{ fontSize: 11, color: "#9CA3AF" }}>
            {bottomSheetOpen ? "▼" : "▲"}
          </span>
        </button>

        {bottomSheetOpen && (
          <div
            style={{
              maxHeight: "60vh",
              overflowY: "auto",
              background: "#FAFAF7",
            }}
          >
            <RailContent {...rest} />
          </div>
        )}
      </div>
      {/* Spacer so content isn't behind sheet tab */}
      <div style={{ height: 52 }} />
    </>
  );
}
